import { DEFAULT_PAGE_SIZE } from '../constants/pagination'
import { Group } from '../types'
import { apiService } from './api.service'

export interface GroupListResponse {
  count: number
  next: string | null
  previous: string | null
  results: Group[]
}

export interface GroupCreateData {
  name: string
  permissions: string[]
}

export interface GroupUpdateData extends Partial<GroupCreateData> {
  id: number
}

class GroupService {
  async listAll(params?: { page?: number; page_size?: number; full_perms?: boolean; [key: string]: any }): Promise<GroupListResponse> {
    const queryParams = new URLSearchParams()
    if (params) {
      Object.keys(params).forEach((key) => {
        const value = params[key]
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString())
        }
      })
    }

    const response = await apiService.get<GroupListResponse>(`groups/?${queryParams.toString()}`)
    return response.data
  }

  async getById(id: number): Promise<Group> {
    const response = await apiService.get<Group>(`groups/${id}/`)
    return response.data
  }

  /** Active users in this group (with email). For DocuSign signers etc. */
  async getGroupUsers(
    groupId: number
  ): Promise<Array<{ id: number; username: string; email: string; first_name: string; last_name: string }>> {
    const response = await apiService.get<{ users: Array<{ id: number; username: string; email: string; first_name: string; last_name: string }> }>(
      `groups/${groupId}/users/`
    )
    return response.data.users || []
  }

  async create(groupData: GroupCreateData): Promise<Group> {
    const response = await apiService.post<Group>('groups/', groupData)
    return response.data
  }

  async update(id: number, groupData: Partial<GroupCreateData>): Promise<Group> {
    const response = await apiService.patch<Group>(`groups/${id}/`, groupData)
    return response.data
  }

  async delete(id: number): Promise<void> {
    await apiService.delete(`groups/${id}/`)
  }

  async listFiltered(
    page: number,
    _ordering?: string,
    sortField?: string,
    sortReverse?: boolean,
    nameFilter?: string,
    includeDocumentCount?: boolean,
    extraParams?: any
  ): Promise<GroupListResponse> {
    const params: any = {
      page,
      page_size: extraParams?.page_size ?? DEFAULT_PAGE_SIZE,
      full_perms: true,
    }

    if (sortField) {
      const prefix = sortReverse ? '-' : ''
      params.ordering = `${prefix}${sortField}`
    }

    if (nameFilter) {
      params.name__icontains = nameFilter
    }

    if (includeDocumentCount) {
      params.include_document_count = true
    }

    if (extraParams) {
      Object.assign(params, extraParams)
    }

    return this.listAll(params)
  }
}

export const groupService = new GroupService()
